/** One session's report page, read from its address: the room, the session, kept fresh while its replay is being made. */
import { useEffect, useState } from "react";
import type { WebinarDetail, WebinarLive } from "../../../shared/types";
import { api } from "../../lib/api";
import { useOnShownAgain } from "../../lib/inApp";
import { replayBuilding } from "./liveWords";

/** How often the page asks after a replay that is still being made. */
const REPLAY_POLL_MS = 8000;

type Phase = "loading" | "ready" | "error";

export function useSessionPage(id: string, sessionId: string) {
  const [detail, setDetail] = useState<WebinarDetail | null>(null);
  const [live, setLive] = useState<WebinarLive | null>(null);
  const [phase, setPhase] = useState<Phase>("loading");

  useEffect(() => {
    let alive = true;
    setPhase("loading");
    Promise.all([api.webinars.get(id), api.board.session(id, sessionId)])
      .then(([web, session]) => {
        if (!alive) return;
        setDetail(web);
        setLive(session);
        setPhase("ready");
      })
      .catch(() => alive && setPhase("error"));
    return () => {
      alive = false;
    };
  }, [id, sessionId]);

  // Shown again by the phone app: the report reads again quietly.
  useOnShownAgain(() => {
    api.board.session(id, sessionId).then(setLive, () => undefined);
  });

  const building = live ? replayBuilding(live) : false;
  useEffect(() => {
    if (!building) return;
    const iv = window.setInterval(() => {
      api.board.session(id, sessionId).then(setLive, () => undefined);
    }, REPLAY_POLL_MS);
    return () => window.clearInterval(iv);
  }, [building, id, sessionId]);

  return { detail, live, phase };
}
